import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, Box, Carousel, Text } from '@stitch-fix/mode-react';
import ShoppableOutfit, {
  ShoppableOutfitPlaceholder,
} from '@stitch-fix/mode-react/ShoppableOutfit';
import { v4 as uuidv4 } from 'uuid';
import { InView, useInView } from 'react-intersection-observer';
import { useViewEvent } from '@stitch-fix/event-reporter';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import { shopPdpPath } from '../../utils/urlUtils';
import WaysToWearItEventTracker from './waysToWearItEventTracker';
import styles from './WaysToWearItCarousel.module.scss';

export const WaysToWearItCarousel = () => {
  const [state, dispatch] = useProductDetailsPageContext();

  const [waysToWearItInViewRef, waysToWearItInView] = useInView({
    triggerOnce: true,
  });
  const setWaysToWearItRefs = useCallback(
    node => {
      waysToWearItInViewRef(node);
    },
    [waysToWearItInViewRef],
  );

  const [waysToWearItViewed, setWaysToWearItViewed] = useState(false);
  const themeMapping = {
    CITRUS: 'citrus',
    NAVY: 'navy',
    WHITE: 'white',
    MINT: 'mint',
  };

  useEffect(() => {
    if (waysToWearItInView && !waysToWearItViewed) {
      setWaysToWearItViewed(true);
      if (state.gated) {
        WaysToWearItEventTracker.trackCarouselSeenInViewport({
          carouselOutfitIds: state.waysToWearIt?.items?.nodes?.map(
            outfit => outfit.id,
          ),
          collection: state.collectionName,
          pdpOutfitId: state.outfitId,
          pdpSkuId: state.selectedSkuId || state.fallbackSkuId,
          externalProductId: state.externalProductId,
          source: state.source,
          subSource: state.subSource,
        });
      } else {
        dispatch({ type: 'WAYS_TO_WEAR_IT_VIEWED' });
      }
    }
  }, [
    waysToWearItInView,
    dispatch,
    waysToWearItViewed,
    state.gated,
    state.waysToWearIt?.items?.nodes,
    state.collectionName,
    state.outfitId,
    state.selectedSkuId,
    state.fallbackSkuId,
    state.externalProductId,
    state.source,
    state.subSource,
  ]);

  const { ref, EventProvider } = useViewEvent({
    name: 'carousel',
    category_id: 'ways_to_wear_it',
    schema: 'category_view',
  });

  // const { reportSelectEvent } = useSelectEvent();

  const outfits = state.waysToWearIt?.items?.nodes;
  const outfitIds = outfits?.map(outfit => outfit.id);

  const fallbackHeadingText = state.gated
    ? 'Ways to wear it, just for you'
    : 'Ways to wear it';
  const headingText = state.waysToWearIt?.title || fallbackHeadingText;
  const heading = <Text setting="title-small">{headingText}</Text>;

  const fallbackSubheadingText = state.gated
    ? 'No endless browsing — we personalized outfits for you based on this item and your style.'
    : 'We created outfits based on this item.';
  const subheadingText =
    state.waysToWearIt?.description || fallbackSubheadingText;

  const onAccordionChange = expanded => {
    // NOTE: This is invalid schema and just causes errors. Commenting out until Algos has time/appetite to specify the schema.
    // reportSelectEvent({
    //   action_name: 'ways_to_wear_it_accordion',
    //   name: 'ways_to_wear_it_accordion',
    //   schema: 'select',
    // });

    if (state.gated) {
      WaysToWearItEventTracker.trackAccordionFolded({
        carouselOutfitIds: outfitIds,
        collection: state.collectionName,
        expanded,
        pdpSkuId: state.selectedSkuId || state.fallbackSkuId,
        externalProductId: state.externalProductId,
        source: state.source,
        subSource: state.subSource,
      });
    } else {
      dispatch({
        type: 'WAYS_TO_WEAR_IT_ACCORDION_CHANGED',
        payload: {
          expanded,
        },
      });
    }
  };

  const buildOutfitImageComponents = outfit =>
    outfit?.items?.map(item => {
      const sku = item?.shopProductVariant;

      if (!sku) {
        // Shop Product Catalog may not have imported the variant yet
        return null;
      }

      // TODO: primaryImage can technically be null here, which would cause an error.
      const primaryImage = sku?.image;
      const fallbackUrl = `${primaryImage?.baseUrl}${primaryImage?.mediaPath}`;
      const imageUrl = primaryImage?.medium || fallbackUrl;

      return {
        id: sku?.id,
        imageUrl,
        altText: primaryImage?.altText,
        label: item.sticker
          ? {
              text: item.sticker.label,
              theme: themeMapping[item.sticker.theme],
            }
          : undefined,
        laydown: item?.laydown,
        url: shopPdpPath(sku?.url, {
          source: state.source,
          subSource: 'ways-to-wear-it',
          outfitId: outfit.id,
          collection: state.collectionName,
          queryId: state.queryId,
        }),
      };
    }) || [];

  const renderOutfit = outfit => {
    return state.gated && !state.isStaticView ? (
      <ShoppableOutfit
        outfitId={outfit.id}
        imageComponents={buildOutfitImageComponents(outfit)?.filter(
          item => !!item?.id,
        )}
        onOutfitItemClick={() => {
          WaysToWearItEventTracker.trackOutfitItemClicked({
            outfitId: state.pdpOutfitId,
            collection: state.collectionName,
            pdpSkuId: state.selectedSkuId || state.fallbackSkuId,
            externalProductId: state.externalProductId,
            source: state.source,
            subSource: state.subSource,
          });
        }}
      />
    ) : (
      <ShoppableOutfit
        outfitId={outfit.id}
        imageComponents={buildOutfitImageComponents(outfit)?.filter(
          item => !!item?.id,
        )}
        onOutfitItemClick={item => {
          dispatch({
            type: 'WAYS_TO_WEAR_IT_OUTFIT_ITEM_CLICKED',
            payload: {
              item,
              outfitId: outfit.id,
            },
          });
        }}
      />
    );
  };

  const renderedOutfits = outfits?.map(outfit => {
    return (
      <InView
        threshold={1}
        key={outfit.id}
        onChange={inView => {
          if (inView) {
            if (state.gated) {
              WaysToWearItEventTracker.trackOutfitSeen({
                carouselOutfitIds: outfitIds,
                collection: state.collectionName,
                outfitId: state.pdpOutfitId,
                pdpSkuId: state.selectedSkuId || state.fallbackSkuId,
                externalProductId: state.externalProductId,
                source: state.source,
                subSource: state.subSource,
              });
            } else {
              dispatch({
                type: 'WAYS_TO_WEAR_IT_OUTFIT_VIEWED',
                payload: {
                  outfitId: outfit.id,
                },
              });
            }
          }
        }}
        triggerOnce
      >
        {state.isStaticView ? (
          <div className={styles.arrow}>
            <div className={styles.blockClicks}>{renderOutfit(outfit)}</div>
          </div>
        ) : (
          renderOutfit(outfit)
        )}
      </InView>
    );
  });

  const placeholderWaysToWearItCarousel = (
    <Accordion
      heading={heading}
      headingLevel="h2"
      onChange={onAccordionChange}
      defaultIsExpanded
      expandIconAlignment="fixed-end"
    >
      <Box pt={{ sm: 0.25, md: 0.5 }} pb={{ sm: 1, md: 1.5 }}>
        <Text setting="body-medium">{subheadingText}</Text>
      </Box>
      <Carousel
        ariaLabel={headingText}
        slideWidth={{ sm: '300px', md: '384px' }}
      >
        {Array(4)
          .fill(null)
          .map(() => (
            <ShoppableOutfitPlaceholder key={uuidv4()} />
          ))}
      </Carousel>
    </Accordion>
  );

  const renderedWaysToWearIt = state.waysToWearIt?.items?.nodes?.length ? (
    <EventProvider>
      <div ref={ref}>
        <Accordion
          heading={heading}
          headingLevel="h2"
          onChange={onAccordionChange}
          defaultIsExpanded
          expandIconAlignment="fixed-end"
        >
          <Box pb={{ sm: 1, md: 1.5 }}>
            <Text setting="body-medium">{subheadingText}</Text>
          </Box>
          <Carousel
            ariaLabel={headingText}
            gutterOverflow={{ sm: 1, lg: 2, xl: 0 }}
            slideWidth={{ sm: '300px', md: '384px' }}
            ref={setWaysToWearItRefs}
          >
            {renderedOutfits}
          </Carousel>
        </Accordion>
      </div>
    </EventProvider>
  ) : null;

  return state.waysToWearItLoading
    ? placeholderWaysToWearItCarousel
    : renderedWaysToWearIt;
};

export default WaysToWearItCarousel;
